import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as widgetDataService from '../common/services/next-gen/WidgetDataService';

/**
 * Hook to fetch count for a specific widget
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCount(widgetKey, enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', widgetKey],
        queryFn: () => widgetDataService.getWidgetCount(widgetKey),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch full data for a specific widget
 * @template T
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useWidgetData(widgetKey, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', widgetKey],
        queryFn: () => widgetDataService.getWidgetData(widgetKey),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch counts for multiple widgets
 * @param {string[]} widgetKeys - Array of widget key identifiers
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCounts(widgetKeys, enabled = true) {
    return useQuery({
        queryKey: ['widgetCounts', widgetKeys],
        queryFn: () => widgetDataService.getWidgetCounts(widgetKeys),
        enabled: enabled && widgetKeys.length > 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch exams without results
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResults(enabled = false) {
    return useWidgetData('exams-without-results', enabled);
}

/**
 * Hook to fetch count of exams without results
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useExamsWithoutResultsCount(enabled = true) {
    return useWidgetCount('exams-without-results', enabled);
}

/**
 * Hook to fetch exams without results filtered by date range
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResultsFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exams-without-results-filtered', request],
        queryFn: () => widgetDataService.getExamsWithoutResultsFiltered(request),
        enabled: enabled && request !== null,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of unperformed exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsCountResponse>}
 */
export function useUnperformedExamsCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'unperformed-exams'],
        queryFn: () => widgetDataService.getUnperformedExamsCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch unperformed exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsDataResponse>}
 */
export function useUnperformedExamsData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'unperformed-exams', page, pageSize],
        queryFn: () => widgetDataService.getUnperformedExamsData(page, pageSize),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to delete an exam
 * @returns {import('@tanstack/react-query').UseMutationResult<{message: string}, Error, {examId: string, reasonForDeletion: string, deleteBothGroupedExams: boolean}>}
 */
export function useDeleteExam() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ examId, reasonForDeletion, deleteBothGroupedExams }) => 
            widgetDataService.deleteExam(examId, reasonForDeletion, deleteBothGroupedExams),
        onSuccess: () => {
            // Invalidate all exam-related widget queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'unperformed-exams'] });
            queryClient.invalidateQueries({ queryKey: ['widgetCount', 'unperformed-exams'] });
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'incomplete-exams'] });
            queryClient.invalidateQueries({ queryKey: ['widgetCount', 'incomplete-exams'] });
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'exams-without-results'] });
            queryClient.invalidateQueries({ queryKey: ['widgetCount', 'exams-without-results'] });
        }
    });
}

/**
 * Hook to fetch count of follow-up tracking records
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingCountResponse>}
 */
export function useFollowUpTrackingCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'follow-up-tracking'],
        queryFn: () => widgetDataService.getFollowUpTrackingCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch follow-up tracking data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingDataResponse>}
 */
export function useFollowUpTrackingData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking', page, pageSize],
        queryFn: () => widgetDataService.getFollowUpTrackingData(page, pageSize),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        keepPreviousData: true // Keep previous data while fetching new page
    });
}

/**
 * Hook to fetch follow-up tracking records filtered by date range, facilities, and BIRADS
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone, selectedFacilities, selectedBirads)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useFollowUpTrackingFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking-filtered', request],
        queryFn: () => widgetDataService.getFollowUpTrackingFiltered(request),
        enabled: enabled && request !== null,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of incomplete exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsCountResponse>}
 */
export function useIncompleteExamsCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'incomplete-exams'],
        queryFn: () => widgetDataService.getIncompleteExamsCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch incomplete exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsDataResponse>}
 */
export function useIncompleteExamsData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'incomplete-exams', page, pageSize],
        queryFn: () => widgetDataService.getIncompleteExamsData(page, pageSize),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        keepPreviousData: true // Keep previous data while fetching new page
    });
}

/**
 * Hook to fetch count of result letters not printed
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedCountResponse>}
 */
export function useResultLetterNotPrintedCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'result-letters-not-printed'],
        queryFn: () => widgetDataService.getResultLetterNotPrintedCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch result letters not printed data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedDataResponse>}
 */
export function useResultLetterNotPrintedData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'result-letters-not-printed', page, pageSize],
        queryFn: () => widgetDataService.getResultLetterNotPrintedData(page, pageSize),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        keepPreviousData: true // Keep previous data while fetching new page
    });
}

/**
 * Hook to fetch tech summary data
 * @param {Object} request - Tech summary request parameters
 * @param {string} request.startDate - Start date (ISO string)
 * @param {string} request.endDate - End date (ISO string)
 * @param {string[]} request.selectedFacilities - Array of facility IDs
 * @param {string[]} request.selectedTechs - Array of technologist user IDs
 * @param {string[]} request.selectedExamTypes - Array of exam type IDs
 * @param {boolean} request.showDetails - Whether to include exam details
 * @param {string} request.ianaTimezone - Client timezone
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<{techName: string, userId: string, totalExams: number, details: Array}[]>}
 */
export function useTechSummaryData(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'tech-summary', request],
        queryFn: () => widgetDataService.getTechSummaryData(request),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}







